import React, { useEffect, useState } from 'react';
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import Dashboard from "@uppy/dashboard";
import Spanish from "@uppy/locales/lib/es_ES";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";

const generateUniqueFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const extension = originalName.includes('.') 
    ? originalName.split('.').pop() 
    : '';
  const baseName = originalName.includes('.') 
    ? originalName.substring(0, originalName.lastIndexOf('.')) 
    : originalName;
  
  const cleanBaseName = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_');
    
  return `${cleanBaseName}_${timestamp}${extension ? `.${extension}` : ''}`;
};

const useUppyWithSupabase = () => {
  const [uppy] = useState(() => 
    new Uppy({
      restrictions: {
        maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB in bytes
        maxNumberOfFiles: 1,
        allowedFileTypes: ['video/*']
      },
      autoProceed: true,
      locale: Spanish
    })
  );

  useEffect(() => {
    const initializeUppy = async () => {
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('token');
      const aplicacionActivacionId = params.get('aplicacionactivacionid');
      const alcance = params.get('alcance');
      const bucketName = 'activacion_aplicacion';

      if (!authToken || !aplicacionActivacionId || !alcance) {
        console.error('Missing required parameters');
        return;
      }

      uppy.use(Tus, {
        endpoint: `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/upload/resumable`,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: {
          authorization: `Bearer ${authToken}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        chunkSize: 6 * 1024 * 1024,
        allowedMetaFields: [
          "bucketName",
          "objectName",
          "contentType",
          "cacheControl",
        ],
        onError: (error: Error) => {
          console.error("Upload error:", error);
        },
      });

      uppy.on('file-added', (file: any) => {
        const uniqueFileName = generateUniqueFileName(file.name);
        file.meta.originalName = file.name;
        file.name = uniqueFileName;
        
        file.meta = {
          ...file.meta,
          bucketName,
          objectName: uniqueFileName,
          contentType: file.type,
        };
      });

      uppy.on('upload-success', async (file: any) => {
        const publicUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${bucketName}/${file.name}`;

        try {
          const functionResponse = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/rpc/activacion_aplicacion_subida_alcance`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                'Apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
              body: JSON.stringify({
                p_aplicacion_activacion_id: aplicacionActivacionId,
                p_alcance: Number(alcance),
                p_evidencia_alcance: publicUrl
              }),
            }
          );

          if (!functionResponse.ok) {
            throw new Error('Failed to update alcance');
          }

          if (params.get('returnUrl')) {
            window.location.href = decodeURIComponent(params.get('returnUrl')!);
          }
        } catch (error) {
          console.error('Error calling Supabase function:', error);
        }
      });
    };

    initializeUppy();

    return () => (uppy as any).close();
  }, [uppy]);

  return uppy;
};

const FileUploaderAlcance = () => {
  const [error, setError] = useState('');
  const uppy = useUppyWithSupabase();

  useEffect(() => {
    uppy.use(Dashboard, {
      inline: true,
      target: "#drag-drop-area",
      height: 450,
      width: '100%',
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: 'Sube un video de máximo 5GB.',
      showRemoveButtonAfterComplete: false,
      hideCancelButton: true,
      doneButtonHandler: null,
    });
  }, [uppy]);

  return (
    <div className="file-uploader-container">
      <div className="file-uploader-box">
        <div className="header-mini">
          <h1 className="upload-title">Sube tu evidencia de alcance</h1>
          <img src="/Logo_Monomo-02.png" alt="Logo" className="logo" />
        </div>
        <div id="drag-drop-area" />
        {error && (
          <p className="error-message">{error}</p>
        )}
      </div>
    </div>
  );
};

export default FileUploaderAlcance;